/**
 * /admin/booking-overrides — Booking Link Overrides admin (2026-08-14).
 *
 * Table + edit-modal CRUD for `property_booking_overrides`.  Admin-only
 * (atlas_admin gate on both `/api/admin/booking-overrides/*` and the
 * `<AdminProtected>` router wrapper).  Isolated from Explore/Atlas
 * card renderers per Cardinal Rule.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Edit3, Trash2, Link as LinkIcon, X,
         Upload, Star, ArrowUp, ArrowDown } from "lucide-react";
import { PROPERTY_TYPES } from "@/constants/propertyTypes";
import OverridePlaceholderImage from "@/components/OverridePlaceholderImage";
import {
  DescriptionPreview, TipStack, FilterPillBadges,
} from "@/components/OverrideEnrichment";

const PROVIDER_LABELS = {
  booking_cj:      "Booking.com / CJ",
  stay22:          "Stay22",
  direct:          "Direct Property",
  other_affiliate: "Other Affiliate",
  other:           "Other",
};
const PROVIDERS = Object.keys(PROVIDER_LABELS);

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
function keyFrom(name, city) { return `${slugify(name)}:${slugify(city)}`; }

const emptyForm = {
  property_key: "", display_name: "", display_name_aliases: "",
  custom_booking_url: "",
  provider: "booking_cj", use_as_primary: true, disable_stay22: false,
  rewards_multiplier: 1, notes: "", match_stay22_property_id: "",
  custom_description: "", advisor_tip: "", traveler_tip: "",
  filter_pills: [], photos: [],
  booking_type: "accommodation",
};

const BOOKING_TYPE_OPTS = [
  { value: "accommodation", label: "Accommodation" },
  { value: "car_rental",    label: "Car rental (global CTA)" },
  { value: "flight",        label: "Flight (global CTA)" },
];

const GLOBAL_KEY_FOR = {
  car_rental: "__global__:car_rental",
  flight:     "__global__:flight",
};

export default function AdminBookingOverridesPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // {mode: 'add'|'edit', form}
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/booking-overrides",
        { params: { q: q.trim() || undefined, limit: 200 } });
      setRows(data.overrides || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openAdd = () => setModal({ mode: "add", form: { ...emptyForm } });
  const openEdit = (row) => setModal({
    mode: "edit",
    form: {
      property_key:            row.property_key,
      display_name:            row.display_name || "",
      display_name_aliases:    (row.display_name_aliases || []).join("\n"),
      custom_booking_url:      row.custom_booking_url || "",
      provider:                row.provider || "booking_cj",
      use_as_primary:          !!row.use_as_primary,
      disable_stay22:          !!row.disable_stay22,
      rewards_multiplier:      row.rewards_multiplier || 1,
      notes:                   row.notes || "",
      match_stay22_property_id: row.match_stay22_property_id || "",
      custom_description:      row.custom_description || "",
      advisor_tip:             row.advisor_tip || "",
      traveler_tip:            row.traveler_tip || "",
      filter_pills:            row.filter_pills || [],
      photos:                  row.photos || [],
      booking_type:            row.booking_type || "accommodation",
    },
  });

  // Deep-link: `/admin/booking-overrides?edit=<property_key>` — auto
  // opens the matching row's edit modal on mount (or after load).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editKey = searchParams.get("edit");
    if (!editKey || rows.length === 0 || modal) return;
    const target = rows.find((r) => r.property_key === editKey);
    if (target) {
      openEdit(target);
      // Clear the param so a subsequent close/open cycle doesn't loop.
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [rows]);

  return (
    <div data-testid="admin-booking-overrides-page"
         className="min-h-screen bg-neutral-50 text-tb-navy px-4 md:px-6 py-4 space-y-4">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Booking link overrides</h1>
        <p className="text-xs text-neutral-500">
          Per-property CTA override · resolver priority: property_id → slug → Stay22 default
        </p>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="admin-bo-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by display name or property_key"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-neutral-300 outline-none focus:border-tb-navy text-sm bg-white"
          />
        </div>
        <button data-testid="admin-bo-search-btn" onClick={load}
                className="h-9 px-3 rounded-md bg-tb-navy text-white text-sm">
          {loading ? "…" : "Search"}
        </button>
        <button data-testid="admin-bo-add-btn" onClick={openAdd}
                className="h-9 px-3 rounded-md bg-tb-gold text-tb-navy text-sm font-semibold inline-flex items-center gap-1.5 ml-auto">
          <Plus className="w-4 h-4" /> Add override
        </button>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <table data-testid="admin-bo-table" className="w-full text-xs">
          <thead className="bg-neutral-50 text-neutral-600">
            <tr>
              <Th>Display name</Th>
              <Th>property_key</Th>
              <Th>Provider</Th>
              <Th>Multiplier</Th>
              <Th>Primary</Th>
              <Th>Stay22</Th>
              <Th>Active</Th>
              <Th>Updated</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.property_key}
                  data-testid={`admin-bo-row-${r.property_key}`}
                  className="border-t border-neutral-100">
                <Td className="font-medium">{r.display_name}</Td>
                <Td className="font-mono text-[11px] text-neutral-600">{r.property_key}</Td>
                <Td>{PROVIDER_LABELS[r.provider] || r.provider}</Td>
                <Td><Pill>{r.rewards_multiplier || 1}X</Pill></Td>
                <Td>{r.use_as_primary ? <Pill kind="green">yes</Pill> : <Pill kind="gray">no</Pill>}</Td>
                <Td>{r.disable_stay22 ? <Pill kind="amber">disabled</Pill> : <Pill kind="gray">on</Pill>}</Td>
                <Td>{r.active ? <Pill kind="green">yes</Pill> : <Pill kind="gray">soft-deleted</Pill>}</Td>
                <Td className="text-neutral-500">{(r.updated_at || "").slice(0, 10)}</Td>
                <Td className="text-right">
                  <button data-testid={`admin-bo-edit-${r.property_key}`}
                          onClick={() => openEdit(r)}
                          className="p-1 hover:bg-neutral-100 rounded">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  {r.active && (
                    <button data-testid={`admin-bo-delete-${r.property_key}`}
                            onClick={() => setDeleting(r)}
                            className="p-1 hover:bg-neutral-100 rounded ml-1">
                      <Trash2 className="w-3.5 h-3.5 text-tb-coral" />
                    </button>
                  )}
                </Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><Td colSpan={9} className="text-center text-neutral-500 py-6">no overrides yet</Td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && <OverrideModal state={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {deleting && <ConfirmDelete row={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
    </div>
  );
}

function OverrideModal({ state, onClose, onSaved }) {
  const [form, setForm] = useState(state.form);
  const [saving, setSaving] = useState(false);
  const isEdit = state.mode === "edit";

  const setField = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // Auto-suggest property_key from display_name in Add mode.
    if (!isEdit && k === "display_name" && !f._touched_key
        && (f.booking_type || "accommodation") === "accommodation") {
      next.property_key = keyFrom(v, "");
    }
    if (k === "property_key") next._touched_key = true;
    // Switching to a generic booking_type auto-fills the reserved
    // property_key and locks the toggle+multiplier invariants.
    if (k === "booking_type") {
      const bt = v;
      if (bt !== "accommodation") {
        next.property_key       = GLOBAL_KEY_FOR[bt] || "";
        next.use_as_primary     = true;
        next.disable_stay22     = true;
        next.rewards_multiplier = 1;
        // Wipe accommodation-only enrichment when switching types
        // to avoid confusing empty fields lingering on save.
        next.display_name_aliases = "";
        next.custom_description = "";
        next.advisor_tip        = "";
        next.traveler_tip       = "";
        next.filter_pills       = [];
      } else if (!isEdit) {
        // Coming back to accommodation in Add mode — regenerate key
        // from display_name if the user hadn't manually typed one.
        if (!f._touched_key) {
          next.property_key = keyFrom(next.display_name, "");
        }
      }
    }
    return next;
  });
  const isGeneric = (form.booking_type || "accommodation") !== "accommodation";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) return toast.error("display_name required");
    if (!form.property_key.trim()) return toast.error("property_key required");
    if (form.use_as_primary && !form.custom_booking_url.trim())
      return toast.error("custom_booking_url required when use_as_primary=true");
    if (form.custom_booking_url && !/^https?:\/\//i.test(form.custom_booking_url))
      return toast.error("URL must start with http:// or https://");
    if ((form.custom_description || "").length > 2000)
      return toast.error("Description must be ≤ 2000 characters");
    if ((form.advisor_tip || "").length > 1000)
      return toast.error("Advisor tip must be ≤ 1000 characters");
    if ((form.traveler_tip || "").length > 1000)
      return toast.error("Traveler tip must be ≤ 1000 characters");
    const aliasArr = (form.display_name_aliases || "")
      .split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      // Photo reorder must happen server-side BEFORE the parent PATCH
      // so an admin that dragged the cover pin sees consistent order
      // in the row list after save.  We only fire reorder when the
      // photo order actually changed from the initial state.
      if (isEdit && form.photos?.length) {
        try {
          await api.patch(
            `/admin/booking-overrides/${encodeURIComponent(form.property_key)}/photos/reorder`,
            { ordered_urls: form.photos.map((p) => p.url) }
          );
        } catch (rrErr) {
          // Non-fatal — the rest of the save still runs.
          console.warn("photo reorder failed", rrErr);
        }
      }
      const patch = {
        display_name:              form.display_name,
        display_name_aliases:      aliasArr,
        custom_booking_url:        form.custom_booking_url,
        provider:                  form.provider,
        use_as_primary:            form.use_as_primary,
        disable_stay22:            form.disable_stay22,
        rewards_multiplier:        form.rewards_multiplier,
        notes:                     form.notes,
        match_stay22_property_id:  form.match_stay22_property_id,
        custom_description:        form.custom_description,
        advisor_tip:               form.advisor_tip,
        traveler_tip:              form.traveler_tip,
        filter_pills:              form.filter_pills,
        booking_type:              form.booking_type || "accommodation",
      };
      if (isEdit) {
        await api.patch(`/admin/booking-overrides/${encodeURIComponent(form.property_key)}`, patch);
      } else {
        await api.post("/admin/booking-overrides", { property_key: form.property_key, ...patch });
      }
      toast.success(isEdit ? "Override updated" : "Override created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  // Photo uploader — uploads immediately to storage (returns URL),
  // then we hold the returned photo objects in `form.photos`.
  // Reorder/delete stay client-side until the parent Save fires
  // (which then sends `photos/reorder`).  Cover is `photos[0]`.
  const [uploading, setUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);   // in-flight count
  const [lastError, setLastError] = useState("");             // inline banner
  const uploadOne = async (file) => {
    // CRITICAL: our axios instance sets a default `Content-Type:
    // application/json` on every request.  For FormData we MUST
    // force the header to `undefined` so axios computes the correct
    // `multipart/form-data; boundary=...` header itself.  A stuck
    // `application/json` header causes FastAPI to reject the body
    // with 422 (no `file` field parseable) — this is the exact
    // silent-fail path the user hit on prod.
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post(
      `/admin/booking-overrides/${encodeURIComponent(form.property_key)}/photos`,
      fd, { headers: { "Content-Type": undefined } });
    return data;
  };
  // Human-friendly classification of any thrown error from uploadOne.
  // Covers HTTP status ranges, network errors, and CORS blocks so
  // the toast tells the user EXACTLY what went wrong.
  const describeUploadError = (err, file) => {
    // network error (no response at all) — CORS, offline, blocked
    // extension, or Edge Enterprise policy.
    if (!err.response) {
      const msg = err.message || "Network error";
      console.error("[upload] no response", {
        file: file?.name, url: err.config?.url, msg, err,
      });
      return `"${file?.name}" — ${msg} (check connection or browser extension)`;
    }
    const { status, data } = err.response;
    console.error("[upload] http error", {
      file: file?.name, status, url: err.config?.url,
      detail: data?.detail, headers: err.config?.headers,
    });
    const detail = formatApiErrorDetail(data?.detail) || "";
    if (status === 401 || status === 403) {
      return `"${file?.name}" — session expired, please refresh & re-login`;
    }
    if (status === 413) {
      const sizeMb = file ? (file.size / 1024 / 1024).toFixed(1) : "?";
      return `"${file?.name}" — file too large (${sizeMb} MB · max 5 MB)`;
    }
    if (status === 422) {
      return `"${file?.name}" — HTTP 422 · ${detail || "server rejected the file (bad format or wrong header)"}`;
    }
    if (status >= 500) {
      return `"${file?.name}" — HTTP ${status} · server error, try again in a moment`;
    }
    return `"${file?.name}" — HTTP ${status} · ${detail || "upload failed"}`;
  };
  const uploadPhotos = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    if (!isEdit) {
      toast.error("Save the override first, then upload photos.");
      return;
    }
    const existing = (form.photos || []).length;
    const capRemaining = Math.max(0, 10 - existing);
    if (capRemaining === 0) {
      toast.error("Photo cap (10) reached — delete before uploading more");
      return;
    }
    const overCap = list.slice(capRemaining);
    const toUpload = list.slice(0, capRemaining);
    if (overCap.length) {
      toast.warning(
        `${overCap.length} photo${overCap.length === 1 ? "" : "s"} skipped — cap is 10 (you have ${existing}).`
      );
    }
    // Client-side per-file guards.
    const valid = [];
    for (const f of toUpload) {
      if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
        toast.error(`"${f.name}" skipped — only JPEG / PNG / WEBP allowed`, { duration: 8000 });
        continue;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`"${f.name}" skipped — too large (${(f.size/1024/1024).toFixed(1)} MB · max 5 MB)`,
                     { duration: 8000 });
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) return;
    setLastError("");
    setUploading(true);
    setUploadingCount(valid.length);
    toast.info(`Uploading ${valid.length} photo${valid.length === 1 ? "" : "s"}…`,
                { duration: 3000 });
    let ok = 0, failed = 0, latestPhotos = form.photos || [];
    let lastErrText = "";
    for (let i = 0; i < valid.length; i++) {
      const f = valid[i];
      try {
        const data = await uploadOne(f);
        if (data?.photos) { latestPhotos = data.photos; }
        setField("photos", latestPhotos);   // live-update tiles as each lands
        ok += 1;
        toast.success(`Uploaded ${ok} of ${valid.length}: ${f.name}`,
                       { duration: 3500 });
      } catch (e) {
        failed += 1;
        const msg = describeUploadError(e, f);
        lastErrText = msg;
        // Prominent red toast held for 10s so the user has time to
        // read + copy the exact error message.
        toast.error(msg, { duration: 10000 });
      } finally {
        setUploadingCount((c) => Math.max(0, c - 1));
      }
    }
    setUploading(false);
    setUploadingCount(0);
    if (failed > 0) setLastError(lastErrText);
    if (ok > 0 && failed === 0) {
      toast.success(`Done — ${ok} photo${ok === 1 ? "" : "s"} uploaded`,
                     { duration: 3000 });
    } else if (ok === 0 && failed > 0) {
      toast.error(`All ${failed} uploads failed — see errors above`,
                   { duration: 10000 });
    }
  };
  const deletePhoto = async (url) => {
    try {
      const { data } = await api.delete(
        `/admin/booking-overrides/${encodeURIComponent(form.property_key)}/photos`,
        { data: { url } });
      setField("photos", data.photos || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };
  const movePhoto = (from, to) => {
    if (from === to || to < 0) return;
    const next = [...(form.photos || [])];
    if (to >= next.length) return;
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setField("photos", next);
  };
  const setCover = (idx) => movePhoto(idx, 0);

  // Live preview offer — mirrors the resolver's synthetic-offer
  // shape so the CardFace-style preview reflects reality.
  const previewOffer = useMemo(() => {
    const photos = (form.photos || []).map((p) => ({ url: p.url, order: p.order }));
    return {
      property_id: `preview:${form.property_key || "new"}`,
      name: form.display_name || "New Override",
      image: photos[0]?.url || null,
      supplier_offers: [{ supplier: form.provider }],
      booking_override: {
        property_key: form.property_key,
        provider: form.provider,
        rewards_multiplier: form.rewards_multiplier || 1,
        photos,
        custom_description: form.custom_description || null,
        advisor_tip: form.advisor_tip || null,
        traveler_tip: form.traveler_tip || null,
        filter_pills: form.filter_pills || [],
      },
    };
  }, [form]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         data-testid="admin-bo-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <h2 className="font-semibold">{isEdit ? "Edit override" : "Add override"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid md:grid-cols-[1fr_320px] gap-0">
        <form onSubmit={submit} className="p-4 space-y-3">
          <Field label="Booking type"
                 help="Accommodation = standard property override. Car rental / Flight = one GLOBAL CTA tile that always shows on the Explore landing. Only one active card allowed per generic type.">
            <select data-testid="admin-bo-form-booking-type"
                    value={form.booking_type || "accommodation"}
                    onChange={(e) => setField("booking_type", e.target.value)}
                    disabled={isEdit && isGeneric}
                    className={"admin-bo-input " + ((isEdit && isGeneric) ? "bg-neutral-100" : "")}>
              {BOOKING_TYPE_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {isGeneric && (
              <p className="mt-1 text-[11px] text-neutral-500 font-hand"
                 data-testid="admin-bo-generic-hint">
                Global tile — property_key locked to <code>{GLOBAL_KEY_FOR[form.booking_type]}</code>.
                Multiplier locked to <strong>1×</strong> (car rentals and flights always earn 1× regardless of advisor context).
                Toggles locked (primary=on, Stay22=disabled).
              </p>
            )}
          </Field>
          <Field label="Display name">
            <input data-testid="admin-bo-form-display-name" value={form.display_name}
                   onChange={(e) => setField("display_name", e.target.value)}
                   className="admin-bo-input" placeholder={isGeneric ? "Book a Car / Book a Flight" : "Saratoga Casino Hotel"} />
          </Field>
          {!isGeneric && (
          <Field label="Alternate names (one per line, optional)"
                 help="Extra name variants Stay22 may return for the same property. Each line matched against Stay22 offer names to dedup near-duplicates. Example: 'Saratoga Casino & Raceway'.">
            <textarea data-testid="admin-bo-form-aliases"
                      value={form.display_name_aliases}
                      onChange={(e) => setField("display_name_aliases", e.target.value)}
                      className="admin-bo-input font-mono text-[12px]" rows={3}
                      placeholder={"Saratoga Casino & Raceway\nSaratoga Casino Raceway"} />
          </Field>
          )}
          <Field label={`Property key ${isGeneric ? "(auto-locked)" : (isEdit ? "(read-only)" : "(auto-suggested, editable)")}`}>
            <input data-testid="admin-bo-form-property-key" value={form.property_key}
                   readOnly={isEdit || isGeneric}
                   onChange={(e) => setField("property_key", e.target.value)}
                   className={"admin-bo-input font-mono " + (isEdit || isGeneric ? "bg-neutral-100" : "")}
                   placeholder="saratoga-casino-hotel:saratoga-springs" />
          </Field>
          <Field label="Custom booking URL">
            <input data-testid="admin-bo-form-url" value={form.custom_booking_url}
                   onChange={(e) => setField("custom_booking_url", e.target.value)}
                   className="admin-bo-input font-mono text-[11px]"
                   placeholder="https://www.dpbolvw.net/..." />
          </Field>
          <Field label="Booking provider">
            <select data-testid="admin-bo-form-provider" value={form.provider}
                    onChange={(e) => setField("provider", e.target.value)}
                    className="admin-bo-input">
              {PROVIDERS.map((p) => (<option key={p} value={p}>{PROVIDER_LABELS[p]}</option>))}
            </select>
          </Field>
          <div className="flex flex-wrap gap-4 items-center">
            <Toggle testid="admin-bo-form-use-as-primary" label="Use as Primary Booking Link"
                    value={form.use_as_primary} disabled={isGeneric}
                    onChange={(v) => setField("use_as_primary", v)} />
            <Toggle testid="admin-bo-form-disable-stay22" label="Disable Stay22 for This Property"
                    value={form.disable_stay22} disabled={isGeneric}
                    onChange={(v) => setField("disable_stay22", v)} />
          </div>
          <Field label={`Rewards multiplier${isGeneric ? " (locked to 1× for global CTAs)" : ""}`}>
            <input type="number" min={1} step={1} readOnly={isGeneric}
                   title={isGeneric ? "Car rentals and flights always earn 1× regardless of advisor context." : undefined}
                   data-testid="admin-bo-form-multiplier" value={form.rewards_multiplier}
                   onChange={(e) => setField("rewards_multiplier", Math.max(1, parseInt(e.target.value || "1", 10)))}
                   className={"admin-bo-input w-24 " + (isGeneric ? "bg-neutral-100" : "")} />
          </Field>

          {!isGeneric && (
          <>
          {/* ─── enrichment ─── */}
          <div className="pt-3 mt-3 border-t border-neutral-200">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
              Traveler-facing content
            </div>

            <Field label={`Description (${(form.custom_description || "").length}/2000)`}
                   help="Shown on the card (first 140 chars) and in full on the detail modal. Preserves line breaks.">
              <textarea data-testid="admin-bo-form-description"
                        value={form.custom_description}
                        onChange={(e) => setField("custom_description", e.target.value.slice(0, 2000))}
                        className="admin-bo-input" rows={4}
                        placeholder="e.g. Historic casino property in the heart of Saratoga Springs. Track view rooms, on-site spa, seasonal packages." />
            </Field>

            <Field label={`TravelBio Advisor Tip (${(form.advisor_tip || "").length}/1000)`}
                   help="Insider tip from a TravelBio advisor — appears with the 'Advisor Tip' label on the card.">
              <textarea data-testid="admin-bo-form-advisor-tip"
                        value={form.advisor_tip}
                        onChange={(e) => setField("advisor_tip", e.target.value.slice(0, 1000))}
                        className="admin-bo-input" rows={3}
                        placeholder="e.g. Book the Track View suite for opening week — the fireworks display over the paddock is free with any booking." />
            </Field>

            <Field label={`Traveler Tip (${(form.traveler_tip || "").length}/1000)`}
                   help="Community-sourced tip — appears with the 'Traveler Tip' label on the card.">
              <textarea data-testid="admin-bo-form-traveler-tip"
                        value={form.traveler_tip}
                        onChange={(e) => setField("traveler_tip", e.target.value.slice(0, 1000))}
                        className="admin-bo-input" rows={3}
                        placeholder="e.g. Skip the buffet — the Sky View lounge on the top floor has better food and a free small plate with any drink after 5pm." />
            </Field>

            <Field label="Filter pills (multi-select)"
                   help="Which Explore filter chips this property should match. When a user filters by any of these, the override card appears alongside Stay22 results. Vocabulary syncs automatically with the Explore filter sheet.">
              <div className="flex flex-wrap gap-1.5" data-testid="admin-bo-form-pills">
                {PROPERTY_TYPES.map((p) => {
                  const on = (form.filter_pills || []).includes(p.id);
                  return (
                    <button
                      key={p.id} type="button"
                      onClick={() => {
                        const cur = new Set(form.filter_pills || []);
                        if (cur.has(p.id)) cur.delete(p.id); else cur.add(p.id);
                        setField("filter_pills", Array.from(cur));
                      }}
                      data-testid={`admin-bo-form-pill-${p.id}`}
                      className={"px-2.5 py-1 rounded-full border text-[11px] " +
                        (on ? "bg-tb-navy text-white border-tb-navy"
                            : "border-neutral-300 text-neutral-700 hover:border-tb-navy")}
                    >{p.label}</button>
                  );
                })}
              </div>
            </Field>
          </div>

          {/* ─── photos ─── */}
          <div className="pt-3 mt-3 border-t border-neutral-200">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
              Photos ({(form.photos || []).length}/10)
              {uploadingCount > 0 && (
                <span className="ml-2 text-tb-navy normal-case tracking-normal font-semibold"
                      data-testid="admin-bo-photo-upload-status">
                  · Uploading {uploadingCount}…
                </span>
              )}
            </div>
            {lastError && (
              <div
                data-testid="admin-bo-photo-upload-error"
                className="mb-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800"
              >
                <span className="mt-0.5">⚠</span>
                <span className="flex-1 leading-snug">{lastError}</span>
                <button type="button" onClick={() => setLastError("")}
                        className="text-red-800/70 hover:text-red-900 px-1">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {!isEdit && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                Save the override first, then upload photos here.
              </p>
            )}
            <div className="grid grid-cols-3 gap-2" data-testid="admin-bo-form-photos">
              {(form.photos || []).map((p, i) => (
                <div key={p.url}
                     className={"relative rounded-md overflow-hidden border " +
                                 (i === 0 ? "border-tb-gold-bright ring-2 ring-tb-gold-bright/50" : "border-neutral-300")}
                     data-testid={`admin-bo-photo-${i}`}>
                  <img src={p.url} alt="" className="w-full aspect-[4/3] object-cover" />
                  <div className="absolute inset-x-0 bottom-0 flex justify-between items-center gap-1 px-1 py-1 bg-black/50">
                    {i === 0 ? (
                      <span className="inline-flex items-center gap-1 text-[9px] text-tb-gold-bright font-semibold uppercase">
                        <Star className="w-3 h-3 fill-tb-gold-bright" /> Cover
                      </span>
                    ) : (
                      <button type="button"
                              onClick={() => setCover(i)}
                              data-testid={`admin-bo-photo-set-cover-${i}`}
                              className="text-[9px] text-white/90 hover:text-tb-gold-bright uppercase">
                        Set cover
                      </button>
                    )}
                    <div className="flex items-center gap-0.5">
                      <button type="button" onClick={() => movePhoto(i, i-1)}
                              disabled={i === 0}
                              className="text-white/80 disabled:opacity-30 p-0.5">
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button type="button" onClick={() => movePhoto(i, i+1)}
                              disabled={i >= (form.photos.length - 1)}
                              className="text-white/80 disabled:opacity-30 p-0.5">
                        <ArrowDown className="w-3 h-3" />
                      </button>
                      <button type="button" onClick={() => deletePhoto(p.url)}
                              data-testid={`admin-bo-photo-delete-${i}`}
                              className="text-red-300 hover:text-red-100 p-0.5">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {Array.from({ length: uploadingCount }).map((_, i) => (
                <div key={`up-${i}`}
                     data-testid={`admin-bo-photo-uploading-${i}`}
                     className="relative aspect-[4/3] rounded-md border border-tb-navy/25 bg-tb-navy/[0.04] flex flex-col items-center justify-center text-tb-navy/70">
                  <div className="w-6 h-6 rounded-full border-2 border-tb-navy/30 border-t-tb-navy animate-spin mb-1" />
                  <span className="text-[10px] font-hand">Uploading…</span>
                </div>
              ))}
              {isEdit && (form.photos || []).length < 10 && (
                <label
                  data-testid="admin-bo-photo-upload"
                  className="flex flex-col items-center justify-center aspect-[4/3] rounded-md border-2 border-dashed border-neutral-300 hover:border-tb-navy cursor-pointer text-neutral-500 hover:text-tb-navy">
                  {uploading ? "Uploading…" : (
                    <>
                      <Upload className="w-5 h-5 mb-1" />
                      <span className="text-[11px]">Add photos</span>
                      <span className="text-[9px] text-neutral-400 leading-tight text-center px-1">
                        JPG/PNG/WEBP · ≤5MB<br />
                        pick up to {10 - (form.photos || []).length} at once
                      </span>
                    </>
                  )}
                  <input type="file" multiple
                         accept="image/jpeg,image/png,image/webp"
                         className="hidden"
                         data-testid="admin-bo-photo-upload-input"
                         onChange={(e) => { uploadPhotos(e.target.files); e.target.value=""; }} />
                </label>
              )}
            </div>
          </div>
          </>
          )}

          <Field label="Notes (optional)">
            <textarea data-testid="admin-bo-form-notes" value={form.notes}
                      onChange={(e) => setField("notes", e.target.value)}
                      className="admin-bo-input" rows={2} />
          </Field>
          <div className="flex gap-2 justify-end pt-2 border-t border-neutral-100">
            <button type="button" onClick={onClose}
                    className="h-9 px-4 rounded-md border border-neutral-300 text-sm">Cancel</button>
            <button data-testid="admin-bo-form-submit"
                    type="submit" disabled={saving}
                    className="h-9 px-4 rounded-md bg-tb-navy text-white text-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Create override")}
            </button>
          </div>
        </form>

        {/* Live preview panel — matches the traveler-facing CardFace look
            so admins see exactly what the traveler will see. */}
        <aside className="border-l border-neutral-200 bg-neutral-50 p-4 hidden md:block"
               data-testid="admin-bo-live-preview">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
            Live preview
          </div>
          <div className="rounded-2xl bg-white border-2 border-tb-navy/15 overflow-hidden shadow-sm">
            <div className="relative aspect-[4/3] bg-white">
              {previewOffer.image ? (
                <img src={previewOffer.image} alt="" className="w-full h-full object-cover" />
              ) : (
                <OverridePlaceholderImage name={previewOffer.name} />
              )}
            </div>
            <div className="px-4 pt-3 pb-3">
              <h3 className="text-tb-navy text-base leading-tight font-semibold"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                {previewOffer.name}
              </h3>
              <DescriptionPreview text={form.custom_description}
                                    testIdPrefix="admin-bo-preview" />
              <TipStack override={previewOffer.booking_override}
                         testIdPrefix="admin-bo-preview" />
              <FilterPillBadges pills={form.filter_pills}
                                 testId="admin-bo-preview-pills" />
              {form.rewards_multiplier > 1 && (
                <div className="mt-2 text-[11px] font-hand text-tb-navy/85 border-t border-tb-cream-3 pt-2">
                  <span className="font-semibold">
                    Earn {form.rewards_multiplier}X TravelBio Points
                  </span>
                </div>
              )}
            </div>
          </div>
          <p className="mt-3 text-[10px] text-neutral-400 leading-snug">
            Read-only preview. Buttons omitted.
          </p>
        </aside>
        </div>
      </div>
      <style>{`
        [data-testid="admin-bo-modal"] .admin-bo-input {
          width: 100%; padding: 6px 8px; border: 1px solid #d4d4d8;
          border-radius: 6px; font-size: 13px; outline: none; background: #fff;
        }
        [data-testid="admin-bo-modal"] .admin-bo-input:focus { border-color: #1a3a5f; }
      `}</style>
    </div>
  );
}

function ConfirmDelete({ row, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/booking-overrides/${encodeURIComponent(row.property_key)}`);
      toast.success("Override deactivated");
      onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         data-testid="admin-bo-delete-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 space-y-3">
        <h2 className="font-semibold">Deactivate this override?</h2>
        <p className="text-sm text-neutral-600">
          Stay22 will resume as the primary booking source for this property.
        </p>
        <p className="text-sm font-mono bg-neutral-100 p-2 rounded">{row.property_key}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="h-9 px-4 rounded-md border border-neutral-300 text-sm">Cancel</button>
          <button data-testid="admin-bo-delete-confirm" onClick={submit} disabled={busy}
                  className="h-9 px-4 rounded-md bg-tb-coral text-white text-sm font-semibold disabled:opacity-60">
            {busy ? "…" : "Deactivate"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── primitives ─── */
function Th({ children, className = "" }) {
  return <th className={"text-left px-2 py-1.5 font-medium " + className}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={"px-2 py-1.5 align-top " + className}>{children}</td>;
}
function Field({ label, help, children }) {
  return (
    <label className="block text-sm">
      <span className="text-xs text-neutral-600 mb-0.5 block">{label}</span>
      {children}
      {help && <span className="text-[10px] text-neutral-500 mt-0.5 block leading-snug">{help}</span>}
    </label>
  );
}
function Pill({ children, kind = "neutral" }) {
  const map = {
    neutral: "bg-neutral-100 border-neutral-300 text-neutral-700",
    green:   "bg-emerald-50 border-emerald-300 text-emerald-800",
    amber:   "bg-amber-50 border-amber-300 text-amber-800",
    gray:    "bg-neutral-100 border-neutral-200 text-neutral-500",
  };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] border ${map[kind] || map.neutral}`}>{children}</span>;
}
function Toggle({ testid, label, value, onChange, disabled }) {
  return (
    <label className={"flex items-center gap-2 text-sm " + (disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer")}>
      <input data-testid={testid} type="checkbox" checked={value}
             disabled={disabled}
             onChange={(e) => !disabled && onChange(e.target.checked)} />
      {label}
    </label>
  );
}
